// Bridges the file picker, the metadata dialog, and the consumer's
// register callback. Usage:
//
//   const { pending, onPick, onCancel, onConfirm } =
//     useUploadWithMetadata({
//       kindLabel: "Photo",
//       upload: async (file, meta) => {
//         await uploader.upload({ otId, kind: "photo", file,
//           register: ({ storedKey }) =>
//             registerPhoto.mutateAsync({
//               name: meta.name, storedKey, note: meta.description,
//               uploadedBy: meta.uploadedBy,
//             }),
//         })
//       },
//     })
//
// The consumer renders `<UploadButton onPick={onPick} />` and
// `<UploadMetadataDialog pending={pending} onCancel onConfirm />`.

import { useCallback, useState } from "react"

import type {
  PendingFileUpload,
  UploadMetadata,
} from "@/features/transit/components/upload-metadata-dialog"

interface UseUploadWithMetadataOptions {
  // Optional dialog-header tag (translated string).
  kindLabel?: string
  // Performs the actual upload. The dialog stays open (with values
  // preserved) if this throws.
  upload: (file: File, meta: UploadMetadata) => Promise<void>
}

export function useUploadWithMetadata({
  kindLabel,
  upload,
}: UseUploadWithMetadataOptions) {
  const [pending, setPending] = useState<PendingFileUpload | null>(null)

  const onPick = useCallback(
    (file: File) => {
      setPending({ file, kindLabel })
    },
    [kindLabel],
  )

  const onPickMany = useCallback(
    (files: File[]) => {
      // For multi-file uploads we ask metadata for each one in turn.
      // Stage the first; the rest are queued by the consumer.
      if (files.length === 0) return
      setPending({ file: files[0], kindLabel })
    },
    [kindLabel],
  )

  const onCancel = useCallback(() => {
    setPending(null)
  }, [])

  const onConfirm = useCallback(
    async (file: File, meta: UploadMetadata) => {
      await upload(file, meta)
      setPending(null)
    },
    [upload],
  )

  return { pending, onPick, onPickMany, onCancel, onConfirm }
}
